import { Calendar, Loader2, UserCheck, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { assignReviewer } from '@/services/reviewService';
import { listStaffUsers, type StaffListItem } from '@/services/staffService';

import type { AssignmentInfo } from '@/types/submission.types';

interface AssignmentSectionProps {
  applicationId: string;
  assignment?: AssignmentInfo;
  onAssigned: (assignment: AssignmentInfo) => void;
}

export function AssignmentSection({ applicationId, assignment, onAssigned }: AssignmentSectionProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [staffUsers, setStaffUsers] = useState<StaffListItem[]>([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadStaff = useCallback(async () => {
    setIsLoadingStaff(true);
    try {
      const users = await listStaffUsers();
      setStaffUsers(users);
    } finally {
      setIsLoadingStaff(false);
    }
  }, []);

  useEffect(() => {
    if (isEditing && staffUsers.length === 0) {
      void loadStaff();
    }
  }, [isEditing, staffUsers.length, loadStaff]);

  const handleStartEdit = useCallback(() => {
    setSelectedUserId(assignment?.reviewerUserId ?? '');
    setDueAt('');
    setIsEditing(true);
  }, [assignment?.reviewerUserId]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setSelectedUserId('');
    setDueAt('');
  }, []);

  const handleAssign = useCallback(async () => {
    if (!selectedUserId) return;

    setIsSubmitting(true);
    try {
      const result = await assignReviewer({
        applicationId,
        reviewerUserId: selectedUserId,
        dueAt: dueAt || undefined,
      });
      onAssigned({
        reviewerUserId: result.reviewerUserId,
        firstName: result.reviewerFirstName,
        lastName: result.reviewerLastName,
        dueAt: result.dueAt,
      });
      setIsEditing(false);
      toast.success(t('submissionDetail.overview.assignSuccess'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('submissionDetail.overview.assignError');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, selectedUserId, dueAt, onAssigned, t]);

  return (
    <div className='border-t border-gray-200 pt-4 mt-4'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold text-gray-700 flex items-center gap-1.5'>
          <UserCheck size={16} />
          {t('submissionDetail.overview.assignment')}
        </h3>
        {!isEditing ? (
          <button
            type='button'
            onClick={handleStartEdit}
            className='text-xs text-blue-600 hover:text-blue-800 font-medium'>
            {assignment ? t('submissionDetail.overview.reassign') : t('submissionDetail.overview.assignReviewer')}
          </button>
        ) : null}
      </div>

      {/* Current assignment display */}
      {!isEditing ? (
        <div className='text-sm'>
          {assignment ? (
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <span className='font-medium text-gray-900'>
                  {assignment.firstName} {assignment.lastName}
                </span>
              </div>
              {assignment.dueAt ? (
                <div className='flex items-center gap-1.5 text-gray-500'>
                  <Calendar size={14} />
                  <span>
                    {t('submissionDetail.overview.assignmentDueAt')}: {new Date(assignment.dueAt).toLocaleDateString()}
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <span className='text-gray-400 italic'>{t('submissionDetail.overview.unassigned')}</span>
          )}
        </div>
      ) : null}

      {/* Edit form */}
      {isEditing ? (
        <div className='space-y-3'>
          {isLoadingStaff ? (
            <div className='flex items-center gap-2 text-sm text-gray-500'>
              <Loader2 size={14} className='animate-spin' />
              {t('common.loading')}
            </div>
          ) : (
            <>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className={cn(
                  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                  'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
                )}>
                <option value=''>{t('submissionDetail.overview.selectReviewer')}</option>
                {staffUsers.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.firstName} {user.lastName} ({user.role})
                  </option>
                ))}
              </select>

              {staffUsers.length === 0 && !isLoadingStaff ? (
                <p className='text-xs text-gray-400'>{t('submissionDetail.overview.noStaffAvailable')}</p>
              ) : null}

              <div>
                <label htmlFor='assignment-due-date' className='block text-xs text-gray-500 mb-1'>
                  {t('submissionDetail.overview.dueDate')}
                </label>
                <input
                  id='assignment-due-date'
                  type='date'
                  value={dueAt}
                  onChange={(e) => setDueAt(e.target.value)}
                  className={cn(
                    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm',
                    'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
                  )}
                />
              </div>

              <div className='flex gap-2'>
                <button
                  type='button'
                  onClick={() => void handleAssign()}
                  disabled={!selectedUserId || isSubmitting}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white',
                    'bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed',
                  )}>
                  {isSubmitting ? <Loader2 size={14} className='animate-spin' /> : <UserPlus size={14} />}
                  {isSubmitting
                    ? t('submissionDetail.overview.assigning')
                    : t('submissionDetail.overview.assignButton')}
                </button>
                <button
                  type='button'
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className='rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100'>
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
